import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../services/api";
import {
  login as persistLogin,
  logout as persistLogout,
  client,
} from "../../services/auth";
import { useAuth } from "../../contexts/AppContext";
import { clearBannerPopupsSeen } from "../../utils/bannerPopupSeen";
import { getApiErrorMessage } from "../../utils/handleApiError";
import { ClientData } from "../../types/auth";
import { InternalLoginResponse } from "../../types/api/AuthResponse";

interface InternalLoginParams {
  user: string;
  password: string;
}

export const useInternalLogin = () => {
  const queryClient = useQueryClient();
  const { setLoggedIn, setLoading, setError, setClientData } = useAuth();

  return useMutation<InternalLoginResponse, unknown, InternalLoginParams>({
    mutationFn: async ({ user, password }) => {
      setLoading(true);
      setError(null);
      const response = await api.post<InternalLoginResponse>(
        "/cliente/centraleconexao",
        {
          user,
          password,
        }
      );

      if (response.status !== 200) {
        // Se falhar, remove o token
        persistLogout();
        throw new Error("Não foi possível autenticar");
      }

      return response.data;
    },
    onSuccess: data => {
      clearBannerPopupsSeen();

      // Persiste o token
      if (data.token) {
        persistLogin(data.token);
      }

      // A API retorna dados compatíveis com ClientData
      const clientData = data as unknown as ClientData;

      // Salva os dados do cliente no localStorage
      client(clientData);

      // Atualiza o contexto de autenticação
      setClientData(clientData);
      setLoggedIn(true);
      setLoading(false);
      setError(null);

      // Invalida todas as queries para forçar recarregamento após login
      queryClient.invalidateQueries();
    },
    onError: (error: unknown) => {
      // Garante que o token seja removido em caso de erro
      persistLogout();

      // Atualiza o contexto com erro
      const errorMessage = getApiErrorMessage(
        error,
        "Não foi possível autenticar"
      );

      setError(errorMessage);
      setLoading(false);
      setLoggedIn(false);
      setClientData(null);
    },
  });
};
